/**
 * Consent — CMP-agnostic wrapper around Shopify's native Customer Privacy API
 * (window.Shopify.customerPrivacy). This is the ONLY file in the theme allowed
 * to touch window.Shopify.customerPrivacy; every consumer reads consent through
 * the app-state contract (app.get('consent.*')) that this file's adapter
 * (initConsent, below) populates from these helpers.
 *
 * Public surface (see docs/customer-privacy.md):
 *
 *   whenReady(cb)            — ensure the API is loaded (Shopify.loadFeatures +
 *                              poll fallback + 5s timeout), then invoke cb(apiAvailable)
 *                              once. Resolves even if the API never loads
 *                              (apiAvailable=false) so gated features never hang.
 *   hasStatisticsConsent()   — analyticsProcessingAllowed() === true (fail-closed)
 *   hasMarketingConsent()    — marketingAllowed()
 *   hasPreferencesConsent()  — preferencesProcessingAllowed()
 *   hasSaleOfDataConsent()   — saleOfDataAllowed()
 *   getRegulation()          — getRegulation() || ''
 *   onConsentChange(cb)      — document 'visitorConsentCollected' → cb(detail);
 *                              ALSO fires cb once with current state on ready
 *                              (the native event never fires for already-stored
 *                              consent). Returns an unsubscribe fn.
 *   showPreferences()        — privacyBanner.showPreferences() (re-open dialog)
 *   isReady()                — boolean: API present and resolved
 *   initConsent()            — the app-state adapter: subscribes via onConsentChange
 *                              and populates app.set('consent', …) (statistics gate,
 *                              ccpa class, banner dark-theme). Called once by
 *                              application.esm.js at init.
 *
 * Import path note: './log.js' is the built runtime filename (source lives at
 * theme/js/core/log.esm.js; Grunt flattens + strips the .esm suffix on copy).
 *
 * AI: Entirely written by AI
 * Date: 2026-06-19
 * Model: Claude Opus 4.8
 * Updated: 2026-10-01 (Claude Opus 5.5) — the adapter's observer re-binds to the
 *   live <body> on every turbo:load and also watches <head>, so the banner/dialog
 *   Shopify re-renders after a Turbo navigation still gets .consent-theme--dark.
 */

import { log } from './log.js';

var READY_TIMEOUT_MS = 5000;
var POLL_INTERVAL_MS = 100;

// Resolution state — module singletons so whenReady resolves exactly once and
// late callers get an immediate synchronous invocation.
var settled = false; // whenReady has resolved (success OR timeout)
var apiAvailable = false; // customerPrivacy global was present at resolution
var readyCallbacks = [];
var pollTimer = null;
var timeoutTimer = null;
var loadKickedOff = false;

function getCustomerPrivacy() {
  return (window.Shopify && window.Shopify.customerPrivacy) || null;
}

function invokeReadyCb(cb) {
  if (typeof cb === 'function') cb(apiAvailable);
}

function resolve(available) {
  if (settled) return;
  settled = true;
  apiAvailable = available === true;
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (timeoutTimer) {
    clearTimeout(timeoutTimer);
    timeoutTimer = null;
  }
  log(
    '[Consent] ready — Customer Privacy API ' +
      (apiAvailable ? 'available' : 'unavailable (fail-closed fallback)')
  );
  var cbs = readyCallbacks.splice(0);
  cbs.forEach(invokeReadyCb);
}

function kickOff() {
  if (loadKickedOff) return;
  loadKickedOff = true;

  // Already attached (return visit / fast load)?
  if (getCustomerPrivacy()) {
    resolve(true);
    return;
  }

  // Preferred readiness primitive — fires its callback (no error arg) once the
  // consent-tracking-api feature is globally available.
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (!error && getCustomerPrivacy()) resolve(true);
        // On error or still-absent, the poll + timeout below still resolve.
      }
    );
  }

  // Poll fallback — covers stores without loadFeatures and the gap between
  // loadFeatures' callback and the global actually being attached.
  pollTimer = setInterval(function () {
    if (getCustomerPrivacy()) resolve(true);
  }, POLL_INTERVAL_MS);

  // Hard timeout — resolve fail-closed so consent-gated UI never hangs when the
  // API is blocked, unauthorized, or slow.
  timeoutTimer = setTimeout(function () {
    resolve(false);
  }, READY_TIMEOUT_MS);
}

export function whenReady(cb) {
  if (settled) {
    invokeReadyCb(cb);
    return;
  }
  if (typeof cb === 'function') readyCallbacks.push(cb);
  kickOff();
}

export function isReady() {
  return settled && apiAvailable;
}

export function hasStatisticsConsent() {
  var cp = getCustomerPrivacy();
  return !!(
    cp &&
    typeof cp.analyticsProcessingAllowed === 'function' &&
    cp.analyticsProcessingAllowed() === true
  );
}

export function hasMarketingConsent() {
  var cp = getCustomerPrivacy();
  return !!(
    cp &&
    typeof cp.marketingAllowed === 'function' &&
    cp.marketingAllowed() === true
  );
}

export function hasPreferencesConsent() {
  var cp = getCustomerPrivacy();
  return !!(
    cp &&
    typeof cp.preferencesProcessingAllowed === 'function' &&
    cp.preferencesProcessingAllowed() === true
  );
}

export function hasSaleOfDataConsent() {
  var cp = getCustomerPrivacy();
  return !!(
    cp &&
    typeof cp.saleOfDataAllowed === 'function' &&
    cp.saleOfDataAllowed() === true
  );
}

export function getRegulation() {
  var cp = getCustomerPrivacy();
  if (cp && typeof cp.getRegulation === 'function') {
    return cp.getRegulation() || '';
  }
  return '';
}

// Whether the consent banner is still pending a decision (i.e. showing). Used
// by the newsletter popup's layout gate so it doesn't fire behind the banner.
// Defaults to false when the API is absent (no banner to wait on).
export function shouldShowBanner() {
  var cp = getCustomerPrivacy();
  return !!(
    cp &&
    typeof cp.shouldShowBanner === 'function' &&
    cp.shouldShowBanner() === true
  );
}

// Snapshot of the current consent state, shaped like the visitorConsentCollected
// event detail so the on-ready initial fire and the change fires look identical
// to subscribers.
function currentConsentDetail() {
  return {
    analyticsAllowed: hasStatisticsConsent(),
    marketingAllowed: hasMarketingConsent(),
    preferencesAllowed: hasPreferencesConsent(),
    saleOfDataAllowed: hasSaleOfDataConsent(),
  };
}

export function onConsentChange(cb) {
  if (typeof cb !== 'function') return function () {};
  var handler = function (event) {
    cb(event.detail);
  };
  document.addEventListener('visitorConsentCollected', handler);
  // The native event fires only when consent CHANGES, not when the listener is
  // added — so on a return visit (consent already stored) it would never fire.
  // Fire cb once with the current state on ready so subscribers initialize.
  whenReady(function () {
    cb(currentConsentDetail());
  });
  return function () {
    document.removeEventListener('visitorConsentCollected', handler);
  };
}

export function showPreferences() {
  var cp = getCustomerPrivacy();
  var banner = (cp && cp.privacyBanner) || window.privacyBanner;
  if (banner && typeof banner.showPreferences === 'function') {
    banner.showPreferences();
    return true;
  }
  log('[Consent] showPreferences unavailable — privacyBanner not loaded');
  return false;
}

// ─── Adapter ────────────────────────────────────────────────────────────────
// Maps the Customer Privacy API (above) onto the CMP-agnostic app.get('consent.*')
// contract that every downstream gate (app.ga, router pageview, newsletter popup)
// reads — populated on ready and on every consent change. Keeping the adapter in
// this module means the whole consent stack (API wrapper + app-state glue) lives
// in one file, and window.Shopify.customerPrivacy is still touched nowhere else.
//
// AI: Entirely written by AI (extracted from application.esm.js)
// Date: 2026-07-07
// Model: Claude Opus 4.8

var BANNER_ID = 'shopify-pc__banner';
var PREFS_DIALOG_ID = 'shopify-pc__prefs__dialog';
var DARK_CLASS = 'consent-theme--dark';

function isVisible(el) {
  return !!el && window.getComputedStyle(el).display !== 'none';
}

// Ground truth for "a banner is blocking": the native banner is actually in the
// DOM and shown. The Customer Privacy API's shouldShowBanner()/whenReady can race
// the banner render, so we read the element directly — this is what guarantees
// the newsletter popup never overlaps the banner.
function bannerShowing() {
  return isVisible(document.getElementById(BANNER_ID));
}

// Shopify exposes NO color-scheme hook (the dark DOM is byte-identical to the
// light one), so infer dark from the rendered background luminance and stamp a
// class the SCSS hooks to invert the brand controls (see _consent-banner.scss).
function applyTheme(el) {
  if (!el) return;
  var bg = window.getComputedStyle(el).backgroundColor;
  var m = bg.match(/\d+(?:\.\d+)?/g);
  if (!m || m.length < 3) {
    log('[Consent] applyTheme(' + el.id + '): unreadable background "' + bg + '"');
    return;
  }
  // Transparent (alpha 0) means Shopify's injected coloring <style> hasn't painted
  // this element yet — bail rather than misread rgba(0,0,0,0) as "dark". The
  // MutationObserver below re-syncs when the coloring <style> lands, so applyTheme
  // runs again once the real color is present.
  if (m.length >= 4 && +m[3] === 0) {
    log('[Consent] applyTheme(' + el.id + '): background transparent, awaiting observer re-sync');
    return;
  }
  var luminance = (0.2126 * +m[0] + 0.7152 * +m[1] + 0.0722 * +m[2]) / 255;
  var dark = luminance < 0.5;
  el.classList.toggle(DARK_CLASS, dark);
  log(
    '[Consent] applyTheme(' + el.id + '): bg=' + bg + ' luminance=' + luminance.toFixed(3) +
      ' → ' + (dark ? 'dark (.' + DARK_CLASS + ' stamped)' : 'light (.' + DARK_CLASS + ' removed)')
  );
}

// The banner, preferences dialog, AND Shopify's injected color <style> blocks
// (#shopify-pc__banner__styles / __prefs__styles, appended to <head>) all get an
// id that starts with `shopify-pc__`, so match on that prefix. Catching the
// <style> blocks is what closes the dark-mode flash: Shopify can inject the
// coloring style in a separate task from the node, so we must re-sync when the
// colors land, not only when the node appears — otherwise the dark bg paints
// before .consent-theme--dark is stamped (black-on-black buttons).
function touchesConsentUi(nodes) {
  for (var i = 0; i < nodes.length; i++) {
    var n = nodes[i];
    if (n.nodeType !== 1) continue;
    if (
      (n.id && n.id.indexOf('shopify-pc__') === 0) ||
      (n.querySelector &&
        (n.querySelector('#' + BANNER_ID) || n.querySelector('#' + PREFS_DIALOG_ID)))
    ) {
      return true;
    }
  }
  return false;
}

export function initConsent() {
  var app = window.app;
  var htmlEl = document.documentElement;

  function sync() {
    var statistics = hasStatisticsConsent();
    var regulation = getRegulation();
    var ccpaApplies = regulation === 'CCPA' || regulation === 'CPRA';
    app.set('consent', {
      statistics: statistics,
      marketing: hasMarketingConsent(),
      preferences: hasPreferencesConsent(),
      saleOfData: hasSaleOfDataConsent(),
      ccpa: ccpaApplies,
      // ready = no banner is blocking. The newsletter popup gates on this, so its
      // countdown only starts once consent has been dealt with (the banner is
      // gone). Keying on the live DOM (not shouldShowBanner) makes the two
      // mutually exclusive even across the async banner render.
      ready: !bannerShowing(),
    });
    // The analytics-queue flush is application.esm.js's concern — it observes
    // this contract and drains its own gaQueue when consent.statistics lands, so
    // the adapter stays a pure contract-writer and never reaches into the queue.
    htmlEl.classList.toggle('ccpa', ccpaApplies);
    var bannerEl = document.getElementById(BANNER_ID);
    var prefsEl = document.getElementById(PREFS_DIALOG_ID);
    log('[Consent] sync', {
      statistics: statistics,
      regulation: regulation || '(none)',
      ccpa: ccpaApplies,
      bannerShowing: bannerShowing(),
      bannerInDom: !!bannerEl,
      prefsInDom: !!prefsEl,
    });
    applyTheme(bannerEl);
    applyTheme(prefsEl);
  }

  // onConsentChange fires once on ready and again on every decision.
  onConsentChange(sync);

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (
        touchesConsentUi(mutations[i].addedNodes) ||
        touchesConsentUi(mutations[i].removedNodes)
      ) {
        log('[Consent] consent-UI mutation detected → re-sync');
        sync();
        // Re-check before the next paint in case the coloring <style> lands in a
        // different task from the node — stamps the dark class before the dark
        // background is painted.
        window.requestAnimationFrame(sync);
        return;
      }
    }
  });

  // Shopify appends the banner + dialog to <body> and their coloring <style>
  // blocks to <head>, so watch both. Turbo Drive replaces <body> on every
  // navigation (back/forward included) and an observer stays bound to the node it
  // was given, so re-observe the live <body> on each turbo:load. Without this,
  // the banner/dialog Shopify re-renders into the new body is never seen, never
  // gets .consent-theme--dark, and the light-mode brand buttons paint on the dark
  // dialog (the "Save my choices" fill vanishes into the background).
  function observeConsentUi() {
    observer.disconnect();
    observer.observe(document.head, { childList: true });
    observer.observe(document.body, { childList: true });
  }
  observeConsentUi();
  document.addEventListener('turbo:load', observeConsentUi);
  log('[Consent] adapter ready — observing <head> + <body> for the banner');
}
